import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import Search from "@arcgis/core/widgets/Search";
import FieldNames from '../../defaults/FieldNames.json';
import {getAppConfig, getViewServiceUrl} from "../../utils/ReduxHelper";
import UserBookmarksService from "../../services/UserBookmarksService";
import ErrorService from "../../services/ErrorService";
import Bookmark from "@arcgis/core/webmap/Bookmark";
import {v4 as uuidv4} from 'uuid';
import {emitCustomEvent} from "react-custom-events";
import Events from "../../defaults/Events";
import LayerList from '@arcgis/core/widgets/LayerList';
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import Bookmarks from "@arcgis/core/widgets/Bookmarks";
import Expand from "@arcgis/core/widgets/Expand";
import DerivationService from "../../services/DerivationService";
import {isFeaturePostFire} from "../../utils/FeatureHelper";
import {calculateFiscalYearQuarter, getSaveableDate} from "../../utils/DateHelper";
import MapLayerInfo from '../../defaults/MapLayerInfo.json'
import Measurement from "@arcgis/core/widgets/Measurement";
import type MapView from "@arcgis/core/views/MapView";
import {toTitleCaseRegex} from "../../utils/StringUtils";
import {setFilterGeometry, setIsLocationFilterOnly} from "../../redux/slices/filterSlice.jsx";
import store from '../../redux/store.tsx';
import { setShowGlobalLoader } from '../../redux/slices/globalContextSlice.jsx';
import { setIsActual} from "../../redux/slices/detailsSlice";

export function GetSearchWidget(mapView) {
    const config = getAppConfig();
    const rootServiceUrl = getViewServiceUrl();
    const outFieldsArray = [
        FieldNames.featureFields.objectID,
        FieldNames.featureFields.name,
        FieldNames.featureFields.class
    ];

    const outFields = outFieldsArray.join(", ")

    const searchWidget = new Search({
        view: mapView,
        includeDefaultSources: false,
        allPlaceholder: 'Find address or place',
        sources: []
    });

    searchWidget.sources.add({
        layer: new FeatureLayer({
            url: rootServiceUrl + "/" + config.ifprsPolygonsView[config.env].layerId,
            outFields: outFields,
            definitionExpression: `${FieldNames.featureFields.class} = '${FieldNames.classTypes.treatment}'`
        }),
        searchFields: config.search.fields,
        exactMatch: false,
        name: config.searchTitles.estimatedTreatments,
        maxSuggestions: 10,
        suggestionsEnabled: true,
        suggestionTemplate: config.search.suggestionTemplate,
        minSuggestCharacters: 2,
        withinViewEnabled: false,
        resultGraphicEnabled: true,
        popupTemplate: {
            title: `${FieldNames.mapLayers.estimatedTreatments}: {Name}`,
            content: "<div>Popup content for {Name}</div>",
            actions: [
                {
                    title: "View Treatment",
                    id: "popup-view-treatment",
                    className: "esri-icon-review"
                },
                {
                    title: "Zoom To",
                    id: "popup-zoom-to-treatment",
                    className: "esri-icon-zoom-in-magnifying-glass"
                },
                {
                    title: "Copy to Existing Treatment or Activity",
                    id: "copy-polygon",
                    className: "esri-icon-feature-layer",
                    disabled: true
                }
            ],
            overwriteActions: true,
        },
    }, 0);

    searchWidget.sources.add({
        layer: new FeatureLayer({
            url: rootServiceUrl + "/" + config.ifprsPolygonsView[config.env].layerId,
            outFields: outFields,
            definitionExpression: `${FieldNames.featureFields.class} = '${FieldNames.classTypes.activity}'`
        }),
        searchFields: config.search.fields,
        exactMatch: false,
        name: config.searchTitles.estimatedActivities,
        maxSuggestions: 10,
        suggestionsEnabled: true,
        suggestionTemplate: config.search.suggestionTemplate,
        minSuggestCharacters: 2,
        withinViewEnabled: false,
        resultGraphicEnabled: true,
        popupTemplate: {
            title: `${FieldNames.mapLayers.estimatedActivities}: {Name}`,
            content: "<div>Popup content for {Name}</div>",
            actions: [
                {
                    title: "View Activity",
                    id: "popup-view-activity",
                    className: "esri-icon-review"
                },
                {
                    title: "Zoom To",
                    id: "popup-zoom-to-activity",
                    className: "esri-icon-zoom-in-magnifying-glass"
                },
                {
                    title: "Copy to Existing Treatment or Activity",
                    id: "copy-polygon",
                    className: "esri-icon-feature-layer",
                    disabled: true
                }
            ],
            overwriteActions: true,
        },
    }, 1);

    searchWidget.sources.add({
        layer: new FeatureLayer({
            url: rootServiceUrl + "/" + config.ifprsPolygonsView[config.env].layerId,
            outFields: outFields,
            definitionExpression: `${FieldNames.featureFields.class} = '${FieldNames.classTypes.actualTreatment}'`
        }),
        searchFields: config.search.fields,
        exactMatch: false,
        name: config.searchTitles.actualTreatments,
        maxSuggestions: 10,
        suggestionsEnabled: true,
        suggestionTemplate: config.search.suggestionTemplate,
        minSuggestCharacters: 2,
        withinViewEnabled: false,
        resultGraphicEnabled: true,
        popupTemplate: {
            title: `${FieldNames.mapLayers.actualTreatments}: {Name}`,
            content: "<div>Popup content for {Name}</div>",
            actions: [
                {
                    title: "View Treatment",
                    id: "popup-view-actuals-treatment",
                    className: "esri-icon-review"
                },
                {
                    title: "Zoom To",
                    id: "popup-zoom-to-actuals-treatment",
                    className: "esri-icon-zoom-in-magnifying-glass"
                },
                {
                    title: "Copy to Existing Treatment or Activity",
                    id: "copy-polygon",
                    className: "esri-icon-feature-layer",
                    disabled: true
                }
            ],
            overwriteActions: true,
        },
    }, 2);

    searchWidget.sources.add({
        layer: new FeatureLayer({
            url: rootServiceUrl + "/" + config.ifprsPolygonsView[config.env].layerId,
            outFields: outFields,
            definitionExpression: `${FieldNames.featureFields.class} = '${FieldNames.classTypes.actualActivity}'`
        }),
        searchFields: config.search.fields,
        exactMatch: false,
        name: config.searchTitles.actualActivities,
        maxSuggestions: 10,
        suggestionsEnabled: true,
        suggestionTemplate: config.search.suggestionTemplate,
        minSuggestCharacters: 2,
        withinViewEnabled: false,
        resultGraphicEnabled: true,
        popupTemplate: {
            title: `${FieldNames.mapLayers.actualActivities}: {Name}`,
            content: "<div>Popup content for {Name}</div>",
            actions: [
                {
                    title: "View Activity",
                    id: "popup-view-actuals-activity",
                    className: "esri-icon-review"
                },
                {
                    title: "Zoom To",
                    id: "popup-zoom-to-actuals-activity",
                    className: "esri-icon-zoom-in-magnifying-glass"
                },
                {
                    title: "Copy to Existing Treatment or Activity",
                    id: "copy-polygon",
                    className: "esri-icon-feature-layer",
                    disabled: true
                }
            ],
            overwriteActions: true,
        },
    }, 3);

    searchWidget.sources.add({
        url: "https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer",  //TODO: Fix URL hardcode
        name: "US Locator",
        countryCode: 'US',
        singleLineFieldName: "SingleLine",
        outFields: ["Addr_type"],
        localSearchOptions: {
            minScale: 300000,
            distance: 50000
        },
        maxResults: 3,
        maxSuggestions: 3
    }, 4);

    return searchWidget;
}

export function SetSearchWidgetDefinition(whereClause, mapSearchWidget) {
    if (mapSearchWidget && mapSearchWidget.sources && mapSearchWidget.sources.items) {
        const config = getAppConfig();
        const mySources = mapSearchWidget.sources.items;
        const enclosedWhereClause = "(" + whereClause + ")";
        if (mySources && mySources.length) {
            mySources.forEach((source) => {
                if (source.name === config.searchTitles.estimatedTreatments || source.name === config.searchTitles.estimatedActivities ||
                    source.name === config.searchTitles.actualTreatments || source.name === config.searchTitles.actualActivities) {
                    source.filter = {where: enclosedWhereClause}
                }
            });
        }
    }
}

export function SetBookmarkWidget(mapView, bookmarkWidget, userRoleInfo, existingBookmarkData, setMapSnackbarOpen, setMapSnackbarMessage,
                                  setMapSnackbarSeverity, setIsBookmarksLoaded, userInfo, setExistingBookmarkData) {
    bookmarkWidget.on("bookmark-edit", (event) => {
        const newBookmark = {
            OBJECTID: event.bookmark.OBJECTID,
            [FieldNames.userBookmarks.usersJoinID]: userRoleInfo[0][FieldNames.userBookmarks.usersJoinID],
            Name: event.bookmark.name,
            Latitude: event.bookmark.viewpoint.targetGeometry.center.latitude,
            Longitude: event.bookmark.viewpoint.targetGeometry.center.longitude,
            Rotation: event.bookmark.viewpoint.rotation,
            Scale: event.bookmark.viewpoint.scale,
            Thumbnail: event.bookmark.thumbnail.url
        }

        UserBookmarksService.updateBookmark(newBookmark).then(response => {
            if (response && response.updateResults && response.updateResults.length > 0 && response.updateResults[0].success) {
                setIsBookmarksLoaded(true);
                setMapSnackbarOpen(true);
                setMapSnackbarMessage(`Bookmark '${newBookmark[FieldNames.userBookmarks.name]}' was successfully updated`);
                setMapSnackbarSeverity("success")
            } else {
                setIsBookmarksLoaded(true);
                setMapSnackbarOpen(true);
                setMapSnackbarMessage(`Bookmark '${newBookmark[FieldNames.userBookmarks.name]}' wasn't saved. Please refresh and try again.`);
                setMapSnackbarSeverity("error")
            }
        }).catch(error => {
            ErrorService.addErrorLogEntry(error);
        });

    });


    bookmarkWidget.on("bookmark-select", (event) => {
        if (mapView && event?.bookmark?.viewpoint) {
            if (event.bookmark.viewpoint.latitude != null &&
                event.bookmark.viewpoint.longitude != null
            ) {
                const centerVal = [event.bookmark.viewpoint.longitude, event.bookmark.viewpoint.latitude];
                mapView.goTo({
                    scale: event.bookmark.viewpoint.scale,
                    rotation: event.bookmark.viewpoint.rotation,
                    center: centerVal
                });
            }
        }
    });

    mapView.when(function () {
        bookmarkWidget.bookmarks.on("change", function (event) {
            event.added.forEach(function (e) {
                UserBookmarksService.getBookmarksByUser(userRoleInfo[0][FieldNames.userBookmarks.usersJoinID]).then(resp => {
                    let isDuplicate = false;
                    if (resp && resp.length > 0) {
                        const justNames = resp.map(x => x.attributes[FieldNames.userBookmarks.name]);
                        if (justNames?.includes(e.name)) {
                            isDuplicate = true;
                        }
                    }
                    if (isDuplicate) {
                        const bookmarkToRemove = bookmarkWidget.bookmarks._items.filter(x => x.uid === e.uid);
                        if (bookmarkToRemove && bookmarkToRemove.length > 0) {
                            bookmarkWidget.bookmarks._items.pop();
                            event.target.items = bookmarkWidget.bookmarks._items;
                            setMapSnackbarOpen(true);
                            setMapSnackbarMessage(`Bookmark '${e.name}' wasn't added. The name entered is already in use for another bookmark.`);
                            setMapSnackbarSeverity("error")
                        }

                    } else {
                        const nextSequenceNumber = resp.length;
                        const newBookmark = {
                            UserBookmarksID: uuidv4(),
                            [FieldNames.userBookmarks.usersJoinID]: userRoleInfo[0]?.[FieldNames.userBookmarks.usersJoinID],
                            Name: e.name,
                            Latitude: e.viewpoint.targetGeometry.center.latitude,
                            Longitude: e.viewpoint.targetGeometry.center.longitude,
                            Rotation: e.viewpoint.rotation,
                            Scale: e.viewpoint.scale,
                            Thumbnail: e.thumbnail.url,
                            SequenceNumber: nextSequenceNumber
                        }

                        UserBookmarksService.addBookmark(newBookmark).then(response => {
                            if (response.addResults && response.addResults.length > 0 && response.addResults[0].success) {
                                const createdBookmark = new Bookmark({
                                    id: response.addResults[0].objectId,
                                    sequenceNumber: newBookmark[FieldNames.userBookmarks.sequenceNumber],
                                    name: newBookmark[FieldNames.userBookmarks.name],
                                    thumbnail: {
                                        url: newBookmark[FieldNames.userBookmarks.thumbnail]
                                    },
                                    viewpoint: {
                                        rotation: newBookmark[FieldNames.userBookmarks.rotation],
                                        scale: newBookmark[FieldNames.userBookmarks.scale],
                                        latitude: newBookmark[FieldNames.userBookmarks.latitude],
                                        longitude: newBookmark[FieldNames.userBookmarks.longitude],
                                    }
                                });
                                const tempExistingBookmarks = JSON.parse(JSON.stringify(existingBookmarkData));
                                tempExistingBookmarks.push(createdBookmark);
                                setExistingBookmarkData(tempExistingBookmarks);
                                setIsBookmarksLoaded(true);
                                setMapSnackbarOpen(true);
                                setMapSnackbarMessage(`Bookmark '${e.name}' was successfully added`);
                                setMapSnackbarSeverity("success")

                            } else {
                                setIsBookmarksLoaded(true);
                                setMapSnackbarOpen(true);
                                setMapSnackbarMessage(`Bookmark '${e.name}' wasn't added. Please refresh and try again.`);
                                setMapSnackbarSeverity("error")
                            }
                        }).catch(error => {
                            ErrorService.addErrorLogEntry(error);
                        });
                    }
                }).catch(error => {
                    ErrorService.addErrorLogEntry(error);
                });

            });
            event.removed.forEach(function (e) {
                UserBookmarksService.deleteBookmarkByName(userRoleInfo[0][FieldNames.userBookmarks.usersJoinID], e.name).then(resp => {
                    if (resp && resp.success) {
                        UserBookmarksService.getBookmarksByUser(userRoleInfo[0][FieldNames.userBookmarks.usersJoinID]).then(remainingBookmarks => {
                            if (remainingBookmarks && remainingBookmarks.length > 0) {
                                let count = 0;
                                let remainingBookmarkListForUpdate = [];
                                remainingBookmarks.forEach(data => {
                                    const bookmarkSequenceInfo = {
                                        attributes: {
                                            OBJECTID: data.attributes[FieldNames.userBookmarks.objectID],
                                            SequenceNumber: count
                                        }
                                    }
                                    remainingBookmarkListForUpdate.push(bookmarkSequenceInfo);
                                    count++;
                                });
                                if (remainingBookmarkListForUpdate && remainingBookmarkListForUpdate.length > 0) {
                                    UserBookmarksService.updateSequenceNumber(remainingBookmarkListForUpdate).then(updateResp => {
                                        if (updateResp && updateResp.updateResults && updateResp.updateResults.length > 0 && updateResp.updateResults[0].success) {
                                            setIsBookmarksLoaded(true);
                                            setMapSnackbarOpen(true);
                                            setMapSnackbarMessage(`Bookmark '${e.name}' was successfully removed`);
                                            setMapSnackbarSeverity("success")

                                        } else {
                                            setIsBookmarksLoaded(true);
                                            setMapSnackbarOpen(true);
                                            setMapSnackbarMessage(`Bookmark '${e.name}' wasn't removed. Please refresh and try again.`);
                                            setMapSnackbarSeverity("error")

                                        }
                                    }).catch(error => {
                                        ErrorService.addErrorLogEntry(error);
                                    });
                                }
                            }
                        }).catch(error => {
                            ErrorService.addErrorLogEntry(error);
                        });
                    } else {
                        setIsBookmarksLoaded(true);
                        setMapSnackbarOpen(true);
                        setMapSnackbarMessage(`Bookmark '${e.name}' wasn't removed. Please refresh and try again.`);
                        setMapSnackbarSeverity("error")

                    }
                }).catch(error => {
                    ErrorService.addErrorLogEntry(error);
                });
            });
            event.moved.forEach(function (e) {
                if (event.moved.indexOf(e) === 0) {
                    const missingIds = event.moved.filter(x => !x.id);
                    //The array hasn't been updated with the latest items so the id field is missing
                    if (missingIds && missingIds.length > 0) {
                        const names = missingIds.map(x => x.name);
                        let itemsWithId = [];
                        event.moved.forEach(eaItem => {
                            if (eaItem.id) {
                                const newObj = {
                                    id: eaItem.id,
                                    name: eaItem.name,
                                    sequenceNumber: eaItem.sequenceNumber
                                }
                                itemsWithId.push(newObj);
                            } else {
                                const newObj = {
                                    id: null,
                                    name: eaItem.name,
                                    sequenceNumber: null
                                }
                                itemsWithId.push(newObj);
                            }
                        })
                        UserBookmarksService.getBookmarksByNamesAndUser(userRoleInfo[0][FieldNames.userBookmarks.usersJoinID], names).then(resp => {
                            //Get all items still missing an id by their name
                            resp.forEach(dbBookmark => {
                                const currentBookmark = itemsWithId.filter(x => x.name === dbBookmark.attributes[FieldNames.userBookmarks.name]);
                                if (currentBookmark && currentBookmark.length > 0) {
                                    const currentIndexInArray = itemsWithId.indexOf(currentBookmark[0]);
                                    itemsWithId[currentIndexInArray][FieldNames.userBookmarks.objectID] = dbBookmark.attributes[FieldNames.userBookmarks.objectID];
                                    itemsWithId[currentIndexInArray].sequenceNumber = dbBookmark.attributes[FieldNames.userBookmarks.sequenceNumber];
                                }
                            })
                            const reorderedBookmarks = [];
                            //They should all have sequenceNumbers now, but in case they don't filter them out
                            const sequenceNumbers = itemsWithId.filter(x => x.sequenceNumber).map(x => x.sequenceNumber);
                            let startingNumber = 0;
                            if (sequenceNumbers && sequenceNumbers.length > 0) {
                                startingNumber = Math.min(...sequenceNumbers);
                            }
                            //Count starts at the lowest number
                            let count = startingNumber;
                            //Prepare for saving
                            itemsWithId.forEach(eaItem => {
                                const bookmarkSequenceInfo = {
                                    attributes: {
                                        OBJECTID: eaItem[FieldNames.userBookmarks.objectID],
                                        SequenceNumber: count
                                    }
                                }
                                reorderedBookmarks.push(bookmarkSequenceInfo);
                                count++;
                            });
                            UserBookmarksService.updateSequenceNumber(reorderedBookmarks).then(resp => {
                                if (resp && resp.updateResults && resp.updateResults.length > 0 && resp.updateResults[0].success) {
                                    setIsBookmarksLoaded(true);
                                    setMapSnackbarOpen(true);
                                    setMapSnackbarMessage(`The bookmarks were successfully reordered`);
                                    setMapSnackbarSeverity("success")
                                } else {
                                    setIsBookmarksLoaded(true);
                                    setMapSnackbarOpen(true);
                                    setMapSnackbarMessage(`The bookmarks weren't reordered. Please refresh and try again.`);
                                    setMapSnackbarSeverity("error")
                                }
                            }).catch(error => {
                                ErrorService.addErrorLogEntry(error);
                            });
                        }).catch(error => {
                            ErrorService.addErrorLogEntry(error);
                        });
                    } else {
                        const reorderedBookmarks = [];
                        const sequenceNumbers = event.moved.map(x => x.sequenceNumber);
                        let startingNumber = 0;
                        if (sequenceNumbers && sequenceNumbers.length > 0) {
                            startingNumber = Math.min(...sequenceNumbers);
                        }
                        let count = startingNumber;

                        event.moved.forEach(eaItem => {
                            const bookmarkSequenceInfo = {
                                attributes: {
                                    OBJECTID: eaItem[FieldNames.userBookmarks.objectID],
                                    SequenceNumber: count
                                }
                            }
                            reorderedBookmarks.push(bookmarkSequenceInfo);
                            count++;
                        });
                        if (reorderedBookmarks && reorderedBookmarks.length > 0) {
                            UserBookmarksService.updateSequenceNumber(reorderedBookmarks).then(resp => {
                                if (resp && resp.updateResults && resp.updateResults.length > 0 && resp.updateResults[0].success) {
                                    setIsBookmarksLoaded(true);
                                    setMapSnackbarOpen(true);
                                    setMapSnackbarMessage(`The bookmarks were successfully reordered`);
                                    setMapSnackbarSeverity("success")
                                } else {
                                    setIsBookmarksLoaded(true);
                                    setMapSnackbarOpen(true);
                                    setMapSnackbarMessage(`The bookmarks weren't reordered. Please refresh and try again.`);
                                    setMapSnackbarSeverity("error")
                                }
                            }).catch(error => {
                                ErrorService.addErrorLogEntry(error);
                            });
                        }
                    }

                }
            });
        })
    })
}

export function getMeasureButtons(view) {
    const measurementWidget = new Measurement({view: view});
    const selectorsDiv = document.createElement("div");
    selectorsDiv.style = "display: flex; flex-direction: row; gap: 5px;";
    selectorsDiv.appendChild(createMeasureButton(view, measurementWidget, 'distance', 'esri-icon-measure-line'));
    selectorsDiv.appendChild(createMeasureButton(view, measurementWidget, 'area', 'esri-icon-measure-area'));
    selectorsDiv.appendChild(createMeasureButton(view, measurementWidget, 'clear', 'esri-icon-trash'));
    return selectorsDiv;
}

export function createMeasureButton(view: MapView, widget: Measurement, type: 'distance' | 'area' | 'clear', iconClassName: string) {
    if (type === 'distance' || type === 'area' || type === 'clear') {
        let button = document.createElement("button");
        button.id = `measure-${type}`;
        button.className = `esri-widget--button esri-interactive ${iconClassName}`;
        button.title = `${toTitleCaseRegex(type)} Measurement Tool`;
        button.onclick = () => {
            if (type === 'clear') {
                widget.clear();
                view.ui.remove(widget);
                return;
            } else {
                widget.activeTool = type;
                view.ui.add(widget, {position: 'top-right', index: 2})
            }
        }
        return button
    } else {
        console.error(`Invalid measure button type: ${type}`);
        return
    }
}

export function MapViewHelper(mapView, setWfftsAttributes, setLegacyAttributes, navigate) {
    reactiveUtils.on(()=> mapView.popup,"selectedFeature", (selectedFeature) => {
        if (selectedFeature) {
            if (selectedFeature.layer && selectedFeature.layer.popupTemplate && selectedFeature.layer.popupTemplate.actions && selectedFeature.layer.popupTemplate.actions.items) {
                selectedFeature.layer.popupTemplate.actions.items.forEach(item => {
                    if ((item.id === 'copy-polygon' || item.id === "copy-wffts-existing-polygon" || item.id === "copy-legacy-existing-polygon" ||
                        item.id === 'copy-wffts-actual-polygon' || item.id === 'copy-new-legacy-actual-polygon') && selectedFeature && selectedFeature.attributes && selectedFeature.attributes[FieldNames.featureFields.isPoint] === FieldNames.yesNo.yes) {
                        item.visible = false;
                    } else {
                        item.visible = true;
                        item.enablled = true;
                    }
                })
            }
        }
    });
    reactiveUtils.on(()=> mapView.popup.viewModel, "trigger-action", (event) => {
        const config = getAppConfig();
        if (event.action.id === "popup-view-treatment") {
            mapView.popup.close();
            emitCustomEvent(Events.DisableExistingIcons, true);
            emitCustomEvent(Events.DisableNewActualIcons, true);
            emitCustomEvent(Events.DisableNewEstimatedIcons, false);
            mapView.goTo({target: mapView.popup.selectedFeature.geometry});
            const treatmentId = mapView.popup.selectedFeature.attributes[FieldNames.featureFields.objectID];
            const isPostFire = isFeaturePostFire(mapView.popup.selectedFeature);
            const entityStringValue = isPostFire ? "pf" : "f";
            navigate(`/${entityStringValue}/treatments/${treatmentId}`);
            store.dispatch(setIsActual(false));

        }

        if (event.action.id === "popup-view-project") {
            mapView.popup.close();
            mapView.goTo({target: mapView.popup.selectedFeature.geometry});
            const projectId = mapView.popup.selectedFeature.attributes[FieldNames.projectFields.objectID];
            const isPostFire = isFeaturePostFire(mapView.popup.selectedFeature);
            const entityStringValue = isPostFire ? "pf" : "f";
            navigate(`/${entityStringValue}/projects/${projectId}`);
        }

        if (event.action.id === "create-new-project-wfigs") {
            (async () => {
                store.dispatch(setShowGlobalLoader(true))
                event.action.disabled = true
                try {
                    var layer = config.WFIGS[config.env].url;
                    const outfields = [
                        FieldNames.wfigsFields.objectID,
                        FieldNames.wfigsFields.discoveryDate,
                        FieldNames.wfigsFields.containmentDate,
                        FieldNames.wfigsFields.projectName,
                        FieldNames.wfigsFields.fireCode,
                        FieldNames.wfigsFields.irwinID,
                        FieldNames.wfigsFields.finalAcres,
                        FieldNames.wfigsFields.sourceSystemName,
                        FieldNames.wfigsFields.sourceSystemID
                    ]
                    const projectObjectId = mapView.popup.selectedFeature.attributes[FieldNames.wfigsFields.objectID];
                    var queryObj = {
                        where: "OBJECTID = " + projectObjectId,
                        outFields: outfields,
                        returnGeometry: true,
                        f: "json"
                    }
                    const fullData = await DerivationService.fetchDataWithRetry(layer, queryObj);
                    const attributesFromService = fullData?.features?.[0]?.attributes;
                    const fireFormattedDate = attributesFromService[FieldNames.wfigsFields.discoveryDate] ? getSaveableDate(attributesFromService[FieldNames.wfigsFields.discoveryDate]) : null
                    const containmentFormattedDate = attributesFromService[FieldNames.wfigsFields.containmentDate] ? getSaveableDate(attributesFromService[FieldNames.wfigsFields.containmentDate]) : null
                    let ignitionFiscalYear = fireFormattedDate ? calculateFiscalYearQuarter(fireFormattedDate, true) : null;
                    let attributes = {
                        [FieldNames.projectFields.entityType]: FieldNames.internalEntityTypes.postFire,
                        [FieldNames.projectFields.isFirePerimeter]: FieldNames.yesNo.yes,
                        [FieldNames.projectFields.fireDiscoveryDate]: fireFormattedDate,
                        [FieldNames.projectFields.containmentDate]: containmentFormattedDate,
                        [FieldNames.projectFields.ignitionFiscalYear]: ignitionFiscalYear,
                        [FieldNames.projectFields.fireName]: attributesFromService?.[FieldNames.wfigsFields.projectName],
                        [FieldNames.projectFields.name]: attributesFromService?.[FieldNames.wfigsFields.projectName],
                        [FieldNames.projectFields.fireCode]: attributesFromService?.[FieldNames.wfigsFields.fireCode],
                        [FieldNames.projectFields.irwinID]: attributesFromService?.[FieldNames.wfigsFields.irwinID],
                        [FieldNames.projectFields.wildfireAcres]: attributesFromService?.[FieldNames.wfigsFields.finalAcres],
                        [FieldNames.projectFields.sourceSystemID]: attributesFromService?.[FieldNames.wfigsFields.objectID],
                        [FieldNames.projectFields.sourceSystemName]: attributesFromService?.[FieldNames.wfigsFields.sourceSystemName],
                    }

                    const perimeter = fullData?.features?.[0]?.geometry;
                    const newFeature = {
                        geometry: perimeter.toJSON(),
                        joinFeature: FieldNames.joinFeatureValues.polygon,
                        isProject: true,
                        isPostFire: true,
                        attributes: attributes
                    }
                    if (attributes) {
                        //FieldNames.wfigsFields.projectName gives us attr_IncidentName from the Fire Perimeter Layer which is used for both name and fire name initially
                        window.api.getProjectByNameIrwinIDFireCode(attributesFromService[FieldNames.wfigsFields.projectName], attributesFromService[FieldNames.wfigsFields.irwinID], attributesFromService[FieldNames.wfigsFields.fireCode]).then((existingProjects) => {
                            if (existingProjects?.length) {
                                //Pick the first one - should only every have 1 project in IFPRS with the same name
                                emitCustomEvent(Events.ExistingProject, existingProjects[0]);
                            } else {
                                emitCustomEvent(Events.CreateProject, newFeature);
                            }
                        });
                    }

                } catch (error) {
                    console.error('Error copying feature:', error);
                }

            })();
        }

        if (event.action.id === "create-new-project-ifph") {
            (async () => {
                store.dispatch(setShowGlobalLoader(true))
                event.action.disabled = true
                try {
                    var layer = config.IFPH[config.env].url;
                    //Leaving out containment date as it's the same field as discoveryDate
                    const outfields = [
                        FieldNames.ifphFields.objectID,
                        FieldNames.ifphFields.discoveryDate,
                        FieldNames.ifphFields.projectName,
                        FieldNames.ifphFields.fireCode,
                        FieldNames.ifphFields.irwinID,
                        FieldNames.ifphFields.finalAcres,
                        FieldNames.ifphFields.sourceSystemName,
                        FieldNames.ifphFields.sourceSystemID
                    ]
                    const projectObjectId = mapView.popup.selectedFeature.attributes[FieldNames.ifphFields.objectID];
                    var queryObj = {
                        where: "OBJECTID = " + projectObjectId,
                        outFields: outfields,
                        returnGeometry: true,
                        f: "json"
                    }
                    const fullData = await DerivationService.fetchDataWithRetry(layer, queryObj);
                    const attributesFromService = fullData?.features?.[0]?.attributes;
                    const dateFromService = attributesFromService[FieldNames.ifphFields.discoveryDate];
                    let year;
                    let month;
                    let day;
                    let formattedDate = null;
                    if (dateFromService) {
                        year = dateFromService.slice(0, 4)
                        month = dateFromService.slice(4, 6)
                        day = dateFromService.slice(6, 8)
                        formattedDate = `${year}-${month}-${day}`
                    }
                    let ignitionFiscalYear = formattedDate ? calculateFiscalYearQuarter(formattedDate, true) : null;
                    let attributes = {
                        [FieldNames.projectFields.entityType]: FieldNames.internalEntityTypes.postFire,
                        [FieldNames.projectFields.isFirePerimeter]: FieldNames.yesNo.yes,
                        [FieldNames.projectFields.fireDiscoveryDate]: formattedDate,
                        [FieldNames.projectFields.containmentDate]: formattedDate,
                        [FieldNames.projectFields.ignitionFiscalYear]: ignitionFiscalYear,
                        [FieldNames.projectFields.fireName]: attributesFromService?.[FieldNames.ifphFields.projectName],
                        [FieldNames.projectFields.name]: attributesFromService?.[FieldNames.ifphFields.projectName],
                        [FieldNames.projectFields.fireCode]: attributesFromService?.[FieldNames.ifphFields.fireCode],
                        [FieldNames.projectFields.irwinID]: attributesFromService?.[FieldNames.ifphFields.irwinID],
                        [FieldNames.projectFields.wildfireAcres]: attributesFromService?.[FieldNames.ifphFields.finalAcres],
                        [FieldNames.projectFields.sourceSystemID]: attributesFromService?.[FieldNames.ifphFields.objectID],
                        [FieldNames.projectFields.sourceSystemName]: attributesFromService?.[FieldNames.ifphFields.sourceSystemName]
                    }

                    const perimeter = fullData?.features?.[0]?.geometry;
                    const newFeature = {
                        geometry: perimeter.toJSON(),
                        joinFeature: FieldNames.joinFeatureValues.polygon,
                        isProject: true,
                        isPostFire: true,
                        attributes: attributes
                    }
                    if (attributesFromService) {
                        //FieldNames.wfigsFields.projectName gives us attr_IncidentName from the Fire Perimeter Layer which is used for both name and fire name initially
                        window.api.getProjectByNameIrwinIDFireCode(attributesFromService[FieldNames.ifphFields.projectName], attributesFromService[FieldNames.ifphFields.irwinID], attributesFromService[FieldNames.ifphFields.fireCode]).then((existingProjects) => {
                            if (existingProjects?.length) {
                                //Pick the first one - should only every have 1 project in IFPRS with the same name
                                emitCustomEvent(Events.ExistingProject, existingProjects[0]);
                            } else {
                                emitCustomEvent(Events.CreateProject, newFeature);
                            }
                        });
                    }

                } catch (error) {
                    console.error('Error copying feature:', error);
                }

            })();
        }

        if (event.action.id === "copy-legacy-as-treatment") {
            (async () => {
                try {
                    var layer = config.completedTreatments.url;
                    var queryObj = {
                        where: "OBJECTID = " + mapView.popup.selectedFeature.attributes.objectid,
                        outFields: ["*"],
                        returnGeometry: true,
                        f: "json"
                    }
                    const fullData = await DerivationService.fetchDataWithRetry(layer, queryObj);
                    const perimeter = fullData.features[0].geometry;
                    const newFeature = {
                        geometry: perimeter.toJSON(),
                        joinFeature: FieldNames.joinFeatureValues.polygon,
                        isTreatment: true,
                        isPostFire: isFeaturePostFire(mapView.popup.selectedFeature.attributes)
                    }
                    emitCustomEvent(Events.EstimatedPolygonCreated, newFeature);
                } catch (error) {
                    console.error('Error copying feature:', error);
                }

            })();

        }

        if (event.action.id === "copy-legacy-as-activity") {
            mapView.popup.close();
            mapView.goTo({target: mapView.popup.selectedFeature.geometry});
            (async () => {
                try {
                    var layer = config.completedTreatments.url;
                    var queryObj = {
                        where: "OBJECTID = " + mapView.popup.selectedFeature.attributes.objectid,
                        outFields: ["*"],
                        returnGeometry: true,
                        f: "json"
                    }
                    const fullData = await DerivationService.fetchDataWithRetry(layer, queryObj);
                    const perimeter = fullData.features[0].geometry;
                    const newFeature = {
                        geometry: perimeter.toJSON(),
                        joinFeature: FieldNames.joinFeatureValues.polygon,
                        isTreatment: false,
                        isPostFire: isFeaturePostFire(mapView.popup.selectedFeature.attributes)
                    }
                    emitCustomEvent(Events.EstimatedPolygonCreated, newFeature);
                } catch (error) {
                    console.error('Error copying feature:', error);
                }
            })();
        }

        if (event.action.id === "copy-wffts-as-treatment") {
            mapView.popup.close();
            mapView.goTo({target: mapView.popup.selectedFeature.geometry});
            (async () => {
                try {
                    var layer = config.WFFTS[config.env].url;
                    var queryObj = {
                        where: "OBJECTID = " + mapView.popup.selectedFeature.attributes.OBJECTID,
                        outFields: ["*"],
                        returnGeometry: true,
                        f: "json"
                    }
                    const fullData = await DerivationService.fetchDataWithRetry(layer, queryObj);
                    const perimeter = fullData.features[0].geometry;
                    const attributes = mapView.popup.selectedFeature.attributes;

                    const fullFeature = {
                        attributes: attributes,
                        geometry: perimeter.toJSON(),
                        isTreatment: true
                    }

                    emitCustomEvent(Events.WfftsShapeCreated, JSON.stringify(fullFeature));
                } catch (error) {
                    console.error('Error copying feature:', error);
                }
            })();
        }

        if (event.action.id === "copy-wffts-as-activity") {
            mapView.popup.close();
            mapView.goTo({target: mapView.popup.selectedFeature.geometry});
            (async () => {
                try {
                    var layer = config.WFFTS[config.env].url;
                    var queryObj = {
                        where: "OBJECTID = " + mapView.popup.selectedFeature.attributes.OBJECTID,
                        outFields: ["*"],
                        returnGeometry: true,
                        f: "json"
                    }
                    const fullData = await DerivationService.fetchDataWithRetry(layer, queryObj);
                    const perimeter = fullData.features[0].geometry;
                    const attributes = mapView.popup.selectedFeature.attributes;

                    // attributes.IsNewActual = true;
                    emitCustomEvent(Events.DisableExistingIcons, true);
                    emitCustomEvent(Events.DisableNewActualIcons, true);
                    emitCustomEvent(Events.DisableNewEstimatedIcons, false);
                    setWfftsAttributes(attributes);
                    const fullFeature = {
                        attributes: attributes,
                        geometry: perimeter.toJSON(),
                        isTreatment: false
                    }
                    emitCustomEvent(Events.WfftsShapeCreated, JSON.stringify(fullFeature));
                } catch (error) {
                    console.error('Error copying feature:', error);
                }
            })();

        }

        if (event.action.id === "copy-wffts-existing-polygon") {
            //Existing Estimated Treatment, Existing Estimated Activity, Existing Actual Treatment, Existing Actual Activity
            mapView.popup.close();
            mapView.goTo({target: mapView.popup.selectedFeature.geometry});
            const attributes = mapView.popup.selectedFeature.attributes;
            emitCustomEvent(Events.DisableExistingIcons, true);
            emitCustomEvent(Events.DisableNewActualIcons, true);
            emitCustomEvent(Events.DisableNewEstimatedIcons, false);
            //Setting this means we have access to the wffts attributes when we click confirm
            setWfftsAttributes(attributes);
            const perimeter = mapView.popup.selectedFeature.geometry.toJSON();
            emitCustomEvent(Events.CopyFromExistingPolygon, perimeter);
        }

        if (event.action.id === "copy-wffts-actual-polygon") {
            //New Actual Treatment, New Actual Activity
            mapView.popup.close();
            mapView.goTo({target: mapView.popup.selectedFeature.geometry});
            emitCustomEvent(Events.GetPlannedFeatureAttributes, (plannedFeature) => {
                const attributes = mapView.popup.selectedFeature.attributes;
                const fullFeature = {
                    attributes: attributes,
                    geometry: mapView.popup.selectedFeature.geometry.toJSON(),
                    plannedObjectID: plannedFeature ? plannedFeature[FieldNames.featureFields.objectID] : null
                }
                emitCustomEvent(Events.DisableExistingIcons, true);
                emitCustomEvent(Events.DisableNewActualIcons, true);
                emitCustomEvent(Events.DisableNewEstimatedIcons, false);
                setWfftsAttributes(attributes);

                emitCustomEvent(Events.GetActualIsTreatment, (isTreatment) => {
                    if (isTreatment) {
                        emitCustomEvent(Events.CopyFromNewActualTreatmentPolygon, JSON.stringify(fullFeature));
                    } else {
                        emitCustomEvent(Events.CopyFromNewActualActivityPolygon, JSON.stringify(fullFeature));
                    }
                });
            })

        }

        if (event.action.id === "copy-legacy-existing-polygon" || event.action.id === "copy-new-legacy-actual-polygon") {
            mapView.popup.close();
            mapView.goTo({target: mapView.popup.selectedFeature.geometry});
            const perimeter = mapView.popup.selectedFeature.geometry;
            const attributes = mapView.popup.selectedFeature.attributes;
            const fullFeature = {
                attributes: attributes,
                geometry: mapView.popup.selectedFeature.geometry.toJSON()
            }
            emitCustomEvent(Events.DisableExistingIcons, true);
            emitCustomEvent(Events.DisableNewActualIcons, true);
            emitCustomEvent(Events.DisableNewEstimatedIcons, false);
            setLegacyAttributes(attributes);
            emitCustomEvent(Events.GetActualIsNew, (isNew) => {
                if (isNew) {
                    emitCustomEvent(Events.GetActualIsTreatment, (isTreatment) => {
                        if (isTreatment) {
                            emitCustomEvent(Events.CopyFromNewLegacyActualTreatmentPolygon, JSON.stringify(fullFeature));
                        } else {
                            emitCustomEvent(Events.CopyFromNewLegacyActualActivityPolygon, JSON.stringify(fullFeature));
                        }
                    })
                } else {
                    emitCustomEvent(Events.CopyPolygon, perimeter);
                }
            })
        }

        if (event.action.id === "copy-wffts-new-actual-polygon") {
            mapView.popup.close();
            mapView.goTo({target: mapView.popup.selectedFeature.geometry});
            const perimeter = mapView.popup.selectedFeature.geometry.toJSON();
            const attributes = mapView.popup.selectedFeature.attributes;
            attributes.IsNewActual = true;
            emitCustomEvent(Events.DisableExistingIcons, true);
            emitCustomEvent(Events.DisableNewActualIcons, true);
            emitCustomEvent(Events.DisableNewEstimatedIcons, false);
            setWfftsAttributes(attributes);
            emitCustomEvent(Events.CopyPolygon, perimeter);
        }


        if (event.action.id === "copy-polygon") {
            mapView.popup.close();
            mapView.goTo({target: mapView.popup.selectedFeature.geometry});
            const perimeter = mapView.popup.selectedFeature.geometry.toJSON();
            emitCustomEvent(Events.CopyPolygon, perimeter);
        }

        if (event.action.id === "popup-zoom-to-treatment") {
            mapView.goTo({target: mapView.popup.selectedFeature.geometry});
            mapView.popup.close();
        }

        if (event.action.id === "popup-zoom-to-project") {
            mapView.goTo({target: mapView.popup.selectedFeature.geometry});
            mapView.popup.close();
        }

        if (event.action.id === "popup-view-activity") {
            mapView.popup.close();
            emitCustomEvent(Events.DisableExistingIcons, true);
            emitCustomEvent(Events.DisableNewActualIcons, true);
            emitCustomEvent(Events.DisableNewEstimatedIcons, false);
            mapView.goTo({target: mapView.popup.selectedFeature.geometry});
            const activityId = mapView.popup.selectedFeature.attributes[FieldNames.featureFields.objectID];
            const isPostFire = isFeaturePostFire(mapView.popup.selectedFeature);
            const entityStringValue = isPostFire ? "pf" : "f";
            navigate(`/${entityStringValue}/activities/${activityId}`);
            store.dispatch(setIsActual(false));
        }

        if (event.action.id === "popup-zoom-to-activity") {
            mapView.goTo({target: mapView.popup.selectedFeature.geometry});
            mapView.popup.close();
        }

        //Actuals
        if (event.action.id === "popup-view-actuals-treatment") {
            mapView.popup.close();
            emitCustomEvent(Events.DisableExistingIcons, true);
            emitCustomEvent(Events.DisableNewActualIcons, true);
            emitCustomEvent(Events.DisableNewEstimatedIcons, false);
            mapView.goTo({target: mapView.popup.selectedFeature.geometry});
            const treatmentId = mapView.popup.selectedFeature.attributes[FieldNames.featureFields.objectID];
            const isPostFire = isFeaturePostFire(mapView.popup.selectedFeature);
            const entityStringValue = isPostFire ? "pf" : "f";
            navigate(`/${entityStringValue}/treatments/${treatmentId}`);
            store.dispatch(setIsActual(true));

        }

        if (event.action.id === "popup-zoom-to-actuals-treatment") {
            mapView.goTo({target: mapView.popup.selectedFeature.geometry});
            mapView.popup.close();
        }

        if (event.action.id === "popup-view-actuals-activity") {
            mapView.popup.close();
            emitCustomEvent(Events.DisableExistingIcons, true);
            emitCustomEvent(Events.DisableNewActualIcons, true);
            emitCustomEvent(Events.DisableNewEstimatedIcons, false);
            mapView.goTo({target: mapView.popup.selectedFeature.geometry});
            const activityId = mapView.popup.selectedFeature.attributes[FieldNames.featureFields.objectID];
            const isPostFire = isFeaturePostFire(mapView.popup.selectedFeature);
            const entityStringValue = isPostFire ? "pf" : "f";
            navigate(`/${entityStringValue}/activities/${activityId}`);
            store.dispatch(setIsActual(true));
        }

        if (event.action.id === "popup-zoom-to-actuals-activity") {
            mapView.goTo({target: mapView.popup.selectedFeature.geometry});
            mapView.popup.close();
        }
    });
}


// TODO - EVAL: Unused component - Delete?
// export function GetLayerList(mapView, mapQueryForTribalInfo, currentMapExtent, setIsTribalLoaded, userInfo,) {
//     let alreadyAddedLayers = [];
//     const parentGroups = MapLayerInfo?.filter(x => x.parentID)?.map(x => x.parentID)
//     const grandparentGroups = MapLayerInfo?.filter(x => x.grandparentID)?.map(x => x.grandparentID)
//     const layerGroupList = parentGroups.concat(grandparentGroups)?.reduce((acc, item) => {
//         if (!acc.includes(item)) {
//             acc.push(item);
//         }
//         return acc;
//     }, []);
//     const initialChildLayerList = MapLayerInfo?.filter(x => x.parentID)?.map(x => x.globalID);
//     const grandparentParentLayerList = MapLayerInfo?.filter(x => x.grandparentID)?.map(x => x.parentID)
//     const completeChildLayerList = initialChildLayerList.concat(grandparentParentLayerList)?.reduce((acc, item) => {
//         if (!acc.includes(item)) {
//             acc.push(item);
//         }
//         return acc;
//     }, []);
//
//     const layerList = new LayerList({
//         container: document.createElement("div"),
//         view: mapView,
//         listItemCreatedFunction: (event) => {
//             const item = event.item;
//             if (item && item.layer && item.layer.title === FieldNames.mapLayers.jurisdictionalUnits) {
//                 item.actionsSections = [[{
//                     title: "Labels",
//                     className: "esri-icon-labels",
//                     id: "labels"
//                 }]];
//             }
//
//             if (item && item.layer && !alreadyAddedLayers?.includes(`${item.layer.globalIdField}`)) {
//                 if (item.layer.title === FieldNames.mapLayers.tribe) {
//                     reactiveUtils.watch(item.layer, 'visible', e => {
//                         if (e) {
//                             if (mapView && item.layer.minScale >= mapView.scale) {
//                                 mapQueryForTribalInfo(currentMapExtent).then((graphics) => {
//                                     item.layer.graphics.removeAll();
//                                     item.layer.addMany(graphics);
//                                     setIsTribalLoaded(true)
//                                 }).catch(error => {
//                                     ErrorService.addErrorLogEntry(error);
//                                 });
//                             } else {
//                                 setIsTribalLoaded(true);
//                                 item.layer.graphics.removeAll();
//                             }
//                         } else {
//                             setIsTribalLoaded(true)
//                             item.layer.graphics.removeAll();
//                         }
//                     });
//                 }
//
//                 if ((layerGroupList.includes(item.layer.globalIdField))) {
//                     reactiveUtils.watch(item.layer, 'visible', e => {
//                         let layerGroup;
//                         if (item.layer.globalIdField === "polygonPF" || item.layer.globalIdField === "linePF" || item.layer.globalIdField === "pointsPF"
//                             || item.layer.globalIdField === "wildlandFireWUI" || item.layer.globalIdField === "wildlandFireAirQuality" || item.layer.globalIdField === "wildlandFireHabitat"
//                         ) {
//                             const layerInfo = MapLayerInfo?.filter(x => x.parentID === item.layer.globalIdField)?.[0]
//                             const layerGroupOuter = mapView.map.layers.find(x => x.globalIdField === layerInfo?.grandparentID);
//                             layerGroup = layerGroupOuter.layers.find(x => x.globalIdField === item.layer.globalIdField)
//                         } else {
//                             layerGroup = mapView.map.layers.find(x => x.globalIdField === item.layer.globalIdField)
//                         }
//
//                         if (layerGroup) {
//                             if (!layerGroup.isChildSettingParentVisibility) {
//                                 if (layerGroup.layers && layerGroup.layers.length > 0) {
//                                     layerGroup.layers.forEach(ea => {
//                                         if (e !== ea.visible) {
//                                             ea.set("isParentSettingChildVisibility", true);
//                                         }
//                                         ea.set("visible", e);
//                                     })
//                                 }
//                             } else {
//                                 layerGroup.set("isChildSettingParentVisibility", false);
//                             }
//                         }
//                     });
//                 }
//
//                 if ((completeChildLayerList.includes(item.layer.globalIdField))) {
//                     reactiveUtils.watch(item.layer, 'visible', e => {
//                         //Lets the toggling of a child control turning on the group/parent layer
//                         //Note toggling off of the child doesn't turn off the group/parent layer
//                         if (!item.layer.isParentSettingChildVisibility) {
//                             let layerGroup;
//                             let layersWithGrandparents = MapLayerInfo?.filter(x => FieldNames.mapGrandParentLayers.includes(x.grandparentID));
//                             let layerParentIDMatch = layersWithGrandparents?.find(x => x.parentID === item.layer.globalIdField);
//                             let layerGlobalIDMatch = layersWithGrandparents?.find(x => x.globalID === item.layer.globalIdField);
//                             if (layerParentIDMatch) {
//                                 layerGroup = mapView.map.layers.find(x => x.globalIdField === layerParentIDMatch.grandparentID);
//                             } else if (layerGlobalIDMatch) {
//                                 const layerInfo = MapLayerInfo?.filter(x => x.globalID === item.layer.globalIdField)?.[0];
//                                 const outerLayerGroup = mapView.map.layers.find(x => x.globalIdField === layerGlobalIDMatch.grandparentID);
//                                 layerGroup = outerLayerGroup?.layers.find(x => x.globalIdField === layerInfo.parentID)
//                             } else {
//                                 const layerInfo = MapLayerInfo?.filter(x => x.globalID === item.layer.globalIdField)?.[0]
//                                 layerGroup = mapView.map.layers.find(x => x.globalIdField === layerInfo?.parentID);
//                             }
//
//                             if (layerGroup && !layerGroup.visible) {
//                                 if (layerGroup.visible !== true) {
//                                     layerGroup.set("isChildSettingParentVisibility", true);
//                                 }
//                                 layerGroup.set("visible", true);
//                             }
//                         } else {
//                             item.layer.set("isParentSettingChildVisibility", false);
//                         }
//                     });
//                 }
//             }
//
//             if (item.layer.legendEnabled) {
//                 item.panel = {
//                     content: "legend",
//                     open: false
//                 };
//             }
//
//             alreadyAddedLayers.push(`${item.layer.globalIdField}`);
//         }
//     });
//
//     layerList.on("trigger-action", function (event) {
//         const item = event.item;
//         if (item.layer.globalIdField === "jurUnits") {
//             if (event.action.id === "labels") {
//                 if (!item.layer.labelsVisible) {
//                     item.layer.labelsVisible = true;
//                 } else {
//                     item.layer.labelsVisible = false;
//                 }
//             }
//         }
//     });
//
//     return new Expand({
//         expandIconClass: "esri-icon-layers",
//         expandTooltip: "Show Layer List",
//         view: mapView,
//         content: layerList.domNode
//     });
// }

export function NewBookmarkWidget(mapView, existingBookmarkData) {
    return new Bookmarks({
        view: mapView,
        // allows bookmarks to be added, edited, or deleted
        editingEnabled: true,
        visibleElements: {
            addBookmarkButton: true,
            editBookmarkButton: true
        },
        defaultCreateOptions: {
            takeScreenshot: true,
            captureViewpoint: true,
            captureTimeExtent: false, // the time extent of the view will not be saved in the bookmark
            screenshotSettings: {
                width: 100,
                height: 100
            },
        },
        defaultEditOptions: {
            takeScreenshot: true,
            captureRotation: true,
            captureScale: true,
            captureViewpoint: true,
            captureTimeExtent: false,
            screenshotSettings: {
                width: 100,
                height: 100
            },
        },
        bookmarks: existingBookmarkData
    });
}

export const getActionHandler = (key, params) => {
    const { handlerInfo, cancelPerimeterCreate, handleClose, dispatch } = params;
    if (handlerInfo[key]) {
        // Cancel any incomplete or completed/unsaved perimeters
        emitCustomEvent(Events.DisableDrawMode, {});
        cancelPerimeterCreate();
        dispatch(setIsLocationFilterOnly(false));
        dispatch(setFilterGeometry(null));
        handlerInfo[key].onClick();
        handleClose();
        handlerInfo[key].setHover(false);
    } else {
        handleClose();
        throw new Error(`No handler found for key: ${key}`);
    }
}